import { UIEvent, useCallback, useEffect, useRef } from "react";

/** Distance (px) from the bottom of the scroll container at which the next page is requested. */
export const NEAR_BOTTOM_THRESHOLD_PX = 50;

type TUseNearBottomFetchParams = {
  /** True when more pages exist; no fetch is triggered when false. */
  hasNextPage?: boolean;
  /** True only while the next page is loading; prevents firing a duplicate request mid-flight. */
  isFetchingNextPage?: boolean;
  /** Loads the next page (e.g. react-query's fetchNextPage). */
  fetchNextPage: () => void;
};

/**
 * Returns an `onScroll` handler that calls `fetchNextPage` once the scroll container is within
 * NEAR_BOTTOM_THRESHOLD_PX of its bottom; an internal ref lock blocks duplicate in-flight requests.
 * Shared by SelectAsync (<div> Paper) and ComboboxMultiAsync (<ul> listbox) — hence UIEvent<HTMLElement>.
 */
export const useNearBottomFetch = ({ hasNextPage, isFetchingNextPage, fetchNextPage }: TUseNearBottomFetchParams) => {
  // Mirror the fetching flag into a ref so the scroll closure reads the latest value without
  // re-subscribing. Boolean() coerces the optional prop's undefined → false.
  const isFetchingRef = useRef(false);
  useEffect(() => {
    isFetchingRef.current = Boolean(isFetchingNextPage);
  }, [isFetchingNextPage]);

  return useCallback(
    (event: UIEvent<HTMLElement>) => {
      const target = event.currentTarget;
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < NEAR_BOTTOM_THRESHOLD_PX;
      if (nearBottom && hasNextPage && !isFetchingRef.current) {
        isFetchingRef.current = true; // optimistic lock until react-query state catches up
        fetchNextPage();
      }
    },
    [hasNextPage, fetchNextPage],
  );
};
